import { camelToSnake, snakeToCamel } from "./text-and-number-utils";

export type CamelCase<S> = S extends string
  ? S extends `${infer P1}_${infer P2}${infer P3}`
    ? `${P1}${Uppercase<P2>}${CamelCase<P3>}`
    : S
  : S;

/**
 * A utility type that converts all keys of a given type to camel-case
 *
 * e.g.
 * ```
 * type CamelCasedType = KeysToCamelCase<{
 *   user_id: string;
 *   first_name?: string;
 * }>;
 * // {
 * //   userId: string;
 * //   firstName?: string;
 * // }
 * ```
 */
export type KeysToCamelCase<T> = T extends object
  ? { [K in keyof T as CamelCase<K>]: T[K] }
  : T;

export type SnakeCase<S> = S extends string
  ? S extends `${infer Head}${infer Tail}`
    ? Tail extends Uncapitalize<Tail>
      ? `${Lowercase<Head>}${SnakeCase<Tail>}`
      : `${Lowercase<Head>}_${SnakeCase<Tail>}`
    : S
  : S;

/** To convert from a typescript type to db snake case. See {@link KeysToCamelCase} */
export type KeysToSnakeCase<T> = T extends object
  ? {
      [K in keyof T as SnakeCase<K>]: T[K];
    }
  : T;
type KeysToOptions = {
  isDeep: boolean;
};
export const keysToSnake = <T extends object>(
  obj: T,
  opts: KeysToOptions = { isDeep: true },
): KeysToSnakeCase<T> =>
  Object.keys(obj).reduce<KeysToSnakeCase<T>>((acc, key) => {
    const newKey = camelToSnake(key);
    if (opts?.isDeep && typeof obj[key] === "object") {
      if (obj[key] instanceof Array) {
        acc[newKey] = obj[key].map((row) =>
          typeof row !== "object" || row === null
            ? row
            : keysToSnake(row, opts),
        );
      } else if (obj[key] instanceof Date) {
        acc[newKey] = obj[key];
      } else {
        acc[newKey] = obj[key] ? keysToSnake(obj[key], opts) : obj[key];
      }
    } else {
      acc[newKey] = obj[key];
    }
    return acc;
  }, {} as KeysToSnakeCase<T>);
export const keysToCamel = <T extends object>(
  obj: T,
  opts: KeysToOptions = { isDeep: true },
): KeysToCamelCase<T> =>
  Object.keys(obj).reduce<KeysToCamelCase<T>>((acc, key) => {
    const newKey = snakeToCamel(key);
    if (opts?.isDeep && typeof obj[key] === "object") {
      if (obj[key] instanceof Array) {
        acc[newKey] = obj[key].map((row) =>
          typeof row !== "object" || row === null
            ? row
            : keysToCamel(row, opts),
        );
      } else if (obj[key] instanceof Date) {
        acc[newKey] = obj[key];
      } else {
        acc[newKey] = obj[key] ? keysToCamel(obj[key], opts) : obj[key];
      }
    } else {
      acc[newKey] = obj[key];
    }
    return acc;
  }, {} as KeysToCamelCase<T>);

export type ObjectDiffOptions = {
  isDeep?: boolean;
  isWarnMuted?: boolean;
};
export const objectDiff = <T = any>(
  obj: Record<string, any>,
  mutation: Record<string, any>,
  opts: ObjectDiffOptions = {},
): T => {
  const { isDeep = false, isWarnMuted = false } = opts || {};
  if (isDeep) {
    throw new TypeError("Deep compare has not be configured");
  }
  if (!mutation || !(typeof obj === "object" && typeof mutation === "object")) {
    throw new TypeError("Compare params are not objects");
  }
  const allKeys = [...new Set([...Object.keys(obj), ...Object.keys(mutation)])];
  return allKeys.reduce((acc, key) => {
    if (key in obj && key in mutation) {
      if (
        ["string", "number", "boolean"].find(
          (jsType) => typeof mutation[key] === jsType,
        ) &&
        mutation[key] !== obj[key]
      ) {
        acc[key] = mutation[key];
      } else if (mutation[key] && typeof mutation[key] === "object") {
        if (
          mutation[key] instanceof Date &&
          obj[key] instanceof Date &&
          mutation[key].getTime() !== obj[key].getTime()
        ) {
          acc[key] = mutation[key];
        } else if (
          mutation[key] instanceof Array &&
          obj[key] instanceof Array &&
          JSON.stringify(mutation[key]) !== JSON.stringify(obj[key])
        ) {
          acc[key] = mutation[key];
        } else if (mutation[key] && !obj[key]) {
          acc[key] = mutation[key];
        } else if (
          mutation[key] &&
          obj[key] &&
          mutation[key].constructor === obj[key].constructor &&
          mutation[key] !== obj[key] &&
          JSON.stringify(mutation[key]) !== JSON.stringify(obj[key])
        ) {
          acc[key] = mutation[key];
        } else {
          if (
            !isWarnMuted &&
            JSON.stringify(mutation[key]) !== JSON.stringify(obj[key])
          ) {
            console.warn(
              "--unhandled key:",
              key,
              "\n { currentData, originalData }:",
              {
                currentData: mutation,
                [`currentData[${key}]`]: mutation[key],
                originalData: obj,
                [`originalData[${key}]`]: obj[key],
              },
            );
          }
        }
      }
    } else if (typeof obj[key] === "undefined" && key in mutation) {
      acc[key] = mutation[key];
    }
    return acc;
  }, {});
};
